'use client';

import { useCallback, useEffect, useState } from 'react';

import Image from 'next/image';
import Link from 'next/link';

interface LandingHeaderProps {
  brandName?: string;
  logoSrc?: string;
  ctaLabel?: string;
  ctaHref?: string;
}

export function LandingHeader({
  brandName = 'Selenos',
  logoSrc,
  ctaLabel = 'Get Started',
  ctaHref,
}: LandingHeaderProps) {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    function handleScroll() {
      setScrolled(window.scrollY > 40);
    }

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleCtaClick = useCallback(
    (e: React.MouseEvent) => {
      if (!ctaHref) {
        e.preventDefault();
        document
          .getElementById('calculator')
          ?.scrollIntoView({ behavior: 'smooth' });
      }
    },
    [ctaHref],
  );

  return (
    <header
      className={`fixed top-0 right-0 left-0 z-50 flex h-14 items-center justify-between px-4 transition-all duration-300 ${
        scrolled ? 'bg-black/80 backdrop-blur-md' : 'bg-transparent'
      }`}
    >
      <Link href="/" className="flex items-center gap-2">
        {logoSrc ? (
          <Image
            src={logoSrc}
            alt={brandName}
            width={120}
            height={32}
            className="h-8 w-auto"
          />
        ) : (
          <span className="text-base font-bold text-white">{brandName}</span>
        )}
      </Link>

      <a
        href={ctaHref ?? '#calculator'}
        onClick={handleCtaClick}
        className="inline-flex h-10 items-center rounded-lg px-5 text-sm font-semibold text-black transition-opacity hover:opacity-90"
        style={{ backgroundColor: 'var(--landing-primary)' }}
      >
        {ctaLabel}
      </a>
    </header>
  );
}
